import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { acceptInvite } from "features/groups/api/Group";
import { useUserSession } from "shared/contexts/UserContext";
import { useTranslation } from "shared/hooks/useTranslation";

export type InviteStatus = "idle" | "loading" | "success" | "error";

interface UseInviteAcceptanceParams {
    token?: string;
    language?: string;
    /** Fallback group name from route params (used by GroupEntrance) */
    fallbackGroupName?: string;
}

export interface UseInviteAcceptanceReturn {
    status: InviteStatus;
    errorMessage: string;
}

function toLegacyGroupRouteSegment(groupName?: string): string {
    return String(groupName || "").trim().replace(/\s/g, "");
}

export function useInviteAcceptance({ token, language, fallbackGroupName }: UseInviteAcceptanceParams): UseInviteAcceptanceReturn {
    const navigate = useNavigate();
    const { profile } = useUserSession();
    const { t } = useTranslation();
    const [status, setStatus] = useState<InviteStatus>("idle");
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (!token || !profile?.username) return;

        let cancelled = false;
        setStatus("loading");

        (async () => {
            try {
                const result = await acceptInvite(token);
                if (cancelled) return;

                const err = (result as { error?: string }).error;
                const msg = (result as { message?: string }).message;
                const group = (result as { group?: { name?: string } }).group;

                if (err) {
                    setStatus("error");
                    setErrorMessage(err);
                    toast.error(err);
                    return;
                }

                setStatus("success");
                if (msg) toast.success(msg);

                const groupId = (result as { group?: { _id?: string } }).group?._id;
                const groupName = groupId || toLegacyGroupRouteSegment(group?.name) || toLegacyGroupRouteSegment(fallbackGroupName);
                const lang = language || "en";
                if (groupName) {
                    navigate(`/${lang}/${profile.username}/groups/${groupName}`, { replace: true });
                } else {
                    navigate(`/${lang}/${profile.username}/groups`, { replace: true });
                }
            } catch (err) {
                if (!cancelled) {
                    setStatus("error");
                    const message = err instanceof Error ? err.message : t("groups.inviteAcceptError", "Failed to accept invite");
                    setErrorMessage(message);
                    toast.error(message);
                }
            }
        })();

        return () => { cancelled = true; };
    }, [token, profile?.username, language, fallbackGroupName, navigate, t]);

    return { status, errorMessage };
}
